import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd, prefersReducedMotion } from '@theme/utilities';

/**
 * Keyframes for the top/bottom hamburger lines: they first squeeze in
 * towards the center, then rotate to form the arms of the "X".
 * @type {Keyframe[]}
 */
const HAMBURGER_OUTER_LINE_KEYFRAMES = [
  { offset: 0, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' },
  { offset: 0.5, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' },
  { offset: 1 },
];

/** @type {Keyframe[]} */
const HAMBURGER_TOP_LINE_KEYFRAMES = [
  { ...HAMBURGER_OUTER_LINE_KEYFRAMES[0], top: '25%', translate: '0 0', rotate: '0deg' },
  { ...HAMBURGER_OUTER_LINE_KEYFRAMES[1], top: '50%', translate: '0 -50%', rotate: '0deg' },
  { ...HAMBURGER_OUTER_LINE_KEYFRAMES[2], top: '50%', translate: '0 -50%', rotate: '45deg' },
];

/** @type {Keyframe[]} */
const HAMBURGER_BOTTOM_LINE_KEYFRAMES = [
  { ...HAMBURGER_OUTER_LINE_KEYFRAMES[0], bottom: '25%', translate: '0 0', rotate: '0deg' },
  { ...HAMBURGER_OUTER_LINE_KEYFRAMES[1], bottom: '50%', translate: '0 50%', rotate: '0deg' },
  { ...HAMBURGER_OUTER_LINE_KEYFRAMES[2], bottom: '50%', translate: '0 50%', rotate: '-45deg' },
];

/** @type {Keyframe[]} */
const HAMBURGER_MIDDLE_LINE_KEYFRAMES = [
  { offset: 0, opacity: 1, scale: '1 1', easing: 'cubic-bezier(0.4, 0, 0.2, 1)' },
  { offset: 0.5, opacity: 1, scale: '0.2 1', easing: 'cubic-bezier(0.4, 0, 0.2, 1)' },
  { offset: 1, opacity: 0, scale: '0.2 1' },
];

const HAMBURGER_ANIMATION_OPTIONS = { duration: 420, fill: /** @type {FillMode} */ ('forwards') };

/**
 * Reverses a set of keyframes so it plays the same motion backwards.
 *
 * `Array.prototype.reverse()` alone isn't enough here: each keyframe has an explicit
 * `offset`, and the Web Animations API requires offsets to be in ascending order, so we
 * reverse the property values but re-apply the original (already ascending) offsets.
 *
 * @param {Keyframe[]} keyframes
 * @returns {Keyframe[]}
 */
function reverseKeyframes(keyframes) {
  const offsets = keyframes.map((frame) => frame.offset);
  return [...keyframes].reverse().map((frame, index) => ({ ...frame, offset: offsets[index] }));
}

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 * @property {HTMLSpanElement[]} [hamburgerLines] - The three lines of the animated hamburger icon, in order (top, middle, bottom).
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {Animation[]} */
  #hamburgerAnimations = [];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    if (details === this.refs.details) this.#animateHamburgerIcon(true);

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    if (details === this.refs.details) this.#animateHamburgerIcon(false);

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Animates the hamburger icon into an "X" (opening) or back into a hamburger (closing).
   *
   * The top and bottom lines first squeeze in towards the center, then rotate to form the
   * arms of the "X"; the middle line shrinks and fades out. Closing plays the same motion
   * in reverse. Skipped when the user prefers reduced motion; the icon's final state is
   * still set correctly via CSS in that case.
   *
   * @param {boolean} isOpening - Whether the drawer is opening (true) or closing (false).
   */
  #animateHamburgerIcon(isOpening) {
    const [top, middle, bottom] = this.refs.hamburgerLines ?? [];
    if (!top || !middle || !bottom || prefersReducedMotion()) return;

    this.#hamburgerAnimations.forEach((animation) => animation.cancel());

    /** @param {Element} element @param {Keyframe[]} keyframes */
    const play = (element, keyframes) =>
      element.animate(isOpening ? keyframes : reverseKeyframes(keyframes), HAMBURGER_ANIMATION_OPTIONS);

    this.#hamburgerAnimations = [
      play(top, HAMBURGER_TOP_LINE_KEYFRAMES),
      play(middle, HAMBURGER_MIDDLE_LINE_KEYFRAMES),
      play(bottom, HAMBURGER_BOTTOM_LINE_KEYFRAMES),
    ];
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
